import * as ser from './SerUtil.js'
import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import {
  valOr,
  extendArray,
  formatNum,
} from '../SharedUtils.js'
import {
  Units,
  DEFAULT_PRECISION,
  getMetricUnits,
  makeValStr,
  roundToNumDecimals,
} from './Units.js'
import { Watcher } from './Watcher.js'
import { gApp } from '../Globals.js'

export let ProjectUnits = makeEnum({
  Imperial: 'Imperial',
  Metric: 'Metric',
})

export let FieldType = {
  Length: 'Length',
  SmallLength: 'SmallLength',
  Area: 'Area',
  SmallArea: 'SmallArea',
  Volume: 'Volume',
  Temp: 'Temp',
  Temperature: 'Temp', // Alias
  TempDiffF: 'TempDiffF',
  Count: 'Count',
  Decimal: 'Decimal',
  // 0-100
  Percent: 'Percent',
  // 0-1
  Ratio: 'Ratio',
  Angle: 'Angle',
  AirFlow: 'AirFlow',
  Load: 'Load',
  LoadMBH: 'LoadMBH',
  RValue: 'RValue',
  UValue: 'UValue',
  Insulation: 'Insulation',
  SoilConductivity: 'SoilConductivity',
  Pressure: 'Pressure',
  SmallPressure: 'SmallPressure',
  Power: 'Power',
  SpinSpeed: 'SpinSpeed',
  WaterFlow: 'WaterFlow',
  Velocity: 'Velocity',
  FrictionLoss: 'FrictionLoss',
  PipeDiameter: 'PipeDiameter',
  WeightPerArea: 'WeightPerArea',
  Density: 'Density',
  RValuePerInch: 'RValuePerInch',
  WaterFlowRate: 'WaterFlowRate',
  HumidityRatio: 'HumidityRatio',
  // Air velocity in fpm (HVAC duct sizing)
  AirVelocity: 'AirVelocity',
  // Duct friction rate in in. w.c. per 100 ft
  DuctFrictionRate: 'DuctFrictionRate',
  SpecificVolume: 'SpecificVolume',
  Enthalpy: 'Enthalpy',
  People: 'People',
  PeoplePer1000ft2: 'PeoplePer1000ft2',
  //Direction: 'Direction',
  String: 'String',
  Select: 'Select',
  TypeSelect: 'TypeSelect',
};

export let SelectFieldStyle = makeEnum({
  Auto: 'Auto',
  Radio: 'Radio',
  Dropdown: 'Dropdown',
})


// export let kFieldTypesData = {};
export let kFieldTypesData = {
  [FieldType.Count]: {
    unit: Units.None,
    min: 0,
  },
  [FieldType.Decimal]: {
    unit: Units.None,
  },
  [FieldType.Length]: {
    unit: Units.ft,
    min: 0,
    allowMin: false,
  },
  [FieldType.SmallLength]: {
    unit: Units.inches,
    min: 0,
    allowMin: false,
  },
  [FieldType.Area]: {
    unit: Units.ft2,
    min: 0,
    allowMin: false,
  },
  [FieldType.SmallArea]: {
    unit: Units.in2,
    min: 0,
    allowMin: false,
  },
  [FieldType.Volume]: {
    unit: Units.ft3,
    min: 0,
    allowMin: false,
  },
  [FieldType.Temp]: {
    unit: Units.F,
  },
  [FieldType.TempDiffF]: {
    unit: Units.TempDiffF,
  },
  [FieldType.Angle]: {
    unit: Units.Degrees,
    min: 0,
    max: 360,
  },
  [FieldType.Percent]: {
    unit: Units.Percent,
    min: 0,
    max: 100,
    allowMin: true,
  },
  [FieldType.Ratio]: {
    unit: Units.Ratio,
    min: 0,
    max: 1.0,
  },
  [FieldType.AirFlow]: {
    unit: Units.AirFlow,
    min: 0,
  },
  [FieldType.Load]: {
    unit: Units.Load,
  },
  [FieldType.LoadMBH]: {
    unit: Units.LoadMBH,
  },
  [FieldType.RValue]: {
    unit: Units.RValue,
    min: 0,
    // R-value must be > 0 b/c we often calculate U=1/R
    allowMin: false,
  },
  [FieldType.UValue]: {
    unit: Units.UValue,
    min: 0,
  },
  [FieldType.Insulation]: {
    unit: Units.Insulation,
    min: 0,
  },
  [FieldType.SoilConductivity]: {
    unit: Units.SoilConductivity,
    min: 0,
  },
  [FieldType.Pressure]: {
    unit: Units.psi,
    min: 0,
  },
  [FieldType.SmallPressure]: {
    unit: Units.in_wc,
    min: 0,
  },
  [FieldType.Power]: {
    unit: Units.Power,
    min: 0,
  },
  [FieldType.SpinSpeed]: {
    unit: Units.SpinSpeed,
    min: 0,
  },
  [FieldType.WaterFlow]: {
    unit: Units.WaterFlow,
    min: 0,
  },
  [FieldType.Velocity]: {
    unit: Units.FtPerSec,
    min: 0,
  },
  [FieldType.FrictionLoss]: {
    unit: Units.FtWcPer100Ft,
    min: 0,
  },
  [FieldType.PipeDiameter]: {
    unit: Units.PipeDiameterIn,
    min: 0,
  },
  [FieldType.WeightPerArea]: {
    unit: Units.PoundsPerFt2,
    min: 0,
    allowMin: false,
  },
  [FieldType.Density]: {
    unit: Units.PoundsPerFt3,
    min: 0,
    allowMin: true,
  },
  [FieldType.RValuePerInch]: {
    unit: Units.RValuePerInch,
    min: 0,
    allowMin: false,
  },
  [FieldType.WaterFlowRate]: {
    unit: Units.PoundsPerHr,
    min: 0,
  },
  [FieldType.HumidityRatio]: {
    unit: Units.HumidityRatio,
    min: 0,
  },
  [FieldType.SpecificVolume]: {
    unit: Units.SpecificVolume,
    min: 0,
  },
  [FieldType.Enthalpy]: {
    unit: Units.Enthalpy,
  },
  [FieldType.People]: {
    unit: Units.People,
    min: 0,
  },
  [FieldType.PeoplePer1000ft2]: {
    unit: Units.PeoplePer1000ft2,
    min: 0,
  },
  // Duct-specific field types
  [FieldType.AirVelocity]: {
    unit: Units.FtPerMin,
    min: 0,
    allowMin: false,
  },
  [FieldType.DuctFrictionRate]: {
    unit: Units.InWcPer100Ft,
    min: 0,
    allowMin: false,
  },
};

/*
Note: subclasses may add more types
*/
export let FieldInputType = makeEnum({
  'Number': 'Number',
  'Select': 'Select',
  'String': 'String',
})

export class Field {
  // For serialization
  static serInfo = {
    type: 'Field',
  }

  constructor(data) {
    this.name = data.name;
    this.key = data.key;
    this.type = valOr(data.type, FieldType.Length);

    this._initDefaultValue(data);
    this._initUnitsAndMinMax(data);
    this._value = valOr(data.value, this.defaultValue);
    // Call this to make sure all default vals are in the proper range
    let clampValue = valOr(data.clampValue, true);
    if (clampValue) {
      this.clampToMinMax();
    }
    // Note - we keep a separate _displayValue field (separate from _value) to handle numeric values
    // where the user-entered value is in the project units (display value) and the standard/imperial units
    // is stored in _value. We can't just store the standard _value only because the conversion to/from a
    // user-entered value is not always exact. If the user enters 6m, this may be X.99999ft. Must keep
    // around the original user-entered value then sync to imperial as we go. We could also just keep
    // the _displayValue only and always derive the _value from it, but this complicates the legacy code.
    // TODO - preserve the displayValue on load, actually!
    this._displayValue = null;
    this._syncDisplayValue();

    // Handle the metric default value, if given
    if (this.getProjectUnits() == ProjectUnits.Metric && 'metricDefaultValue' in data) {
      this._displayValue = data.metricDefaultValue;
      this._syncActualValue();
    }

    // If true, the field will be highlighted and require the 
    // user to enter a value, before it is valid.
    this.requiresInput = valOr(data.requiresInput, false);

    this.choices = data.choices;
    this.choicesFunc = data.choicesFunc;
    this.hiddenChoices = data.hiddenChoices;
    this.lookupValueFunc = data.lookupValueFunc;
    this.selectFieldStyle = valOr(data.selectFieldStyle, SelectFieldStyle.Auto);

    this.visible = valOr(data.visible, true);
    this.isOutput = valOr(data.isOutput, false);
    this.isNA = valOr(data.isNA, false);
    // This is set in output fields to indicate that the output value is NA until the
    // user fills in certain inputs. A '--' will be shown in the UI when true.
    this.waitingInputs = false;

    this.showName = valOr(data.showName, true);
    this.debugOutput = valOr(data.debugOutput, null);

    // Bolded/emphasized in the UI
    this.bold = valOr(data.bold, false);
    // Number of decimals for numeric values
    this.precision = valOr(data.precision, null);

    // Error msg for empty selects
    this.errorWhenEmpty = valOr(data.errorWhenEmpty, null);

    // Misc:
    // The watcher used to register watchEffect handles.
    if (data.watcher) {
      this.watcher = data.watcher;
    } else {
      if (gApp && gApp.proj()) {
        this.watcher = gApp.proj();
      } else {
        this.watcher = Watcher.globalInstance();
      }
    }
    // The project context (which provides some functions to get units, etc.)
    // Defaults to null (which means use gApp.proj())
    this.projectContext = valOr(data.projectContext, null);
    // Used to store other data (should be a simple map)
    this.data = valOr(data.data, {})
    this.metadata = valOr(data.metadata, {})
    // A brief note that gives some info to the AI assistant on how to use this field.
    this.note = valOr(data.note, '')
    // Additional structured data that the AI assistant can use to understand how to use this field.
    // For example, for a window style select field, this might include the list of possible styles and their properties.
    this.notesData = valOr(data.notesData, null)

    // An error msg that is set during an internal update of the field (should only
    // be set on an unexpected internal error).
    this.internalErrorMsg = null

    // A map from unique string key to string error message.
    // Use the setErrorMsg & clearErrorMsg functions to manage this.
    this.errorsMap = new Map(); // key -> msg

    this.serFields = [
      '_value',
      '_displayValue',
      'requiresInput', 
      'data',
    ];
    this.childObjs = '$none'
    this.objInfo = {
      '_name': this.name,
      _uniqueName: true,
      path: data.basePath ? `${data.basePath}/field` : 'field',
    }
  }

  postReadFromJson(jsonData, opts) {
    if ('value' in jsonData) {
      // If this project was using the old format (only a 'value' field), then read that and sync
      // the display value.
      this._value = jsonData.value;
      this._syncDisplayValue();
    }

    // If the display value is way out of sync with the _value, then reset it.
    if (this.isNumber()) {
      let displayValueInStdUnits = Units.convertValue(this._displayValue, this.getDisplayUnits(), this.units);
      if (Math.abs(displayValueInStdUnits - this._value) > 0.01) {
        console.log(`Field ${this.name} - Detected displayValue out of sync. Syncing...`);
        this._syncDisplayValue();
      }
    }
  }

  get value() {
    return this._value;
  }

  set value(newVal) {
    this._value = newVal;
    this._syncDisplayValue();
  }

  // Note - 'stdValue' is an alias for the imperial/standard units value.
  // Same as 'value'.
  get stdValue() {
    return this._value;
  }

  // Note - 'stdValue' is an alias for the imperial/standard units value.
  // Same as 'value'.
  set stdValue(newVal) {
    this._value = newVal;
    this._syncDisplayValue();
  }

  get displayValue() {
    return this._displayValue;
  }

  set displayValue(newVal) {
    this._displayValue = newVal;
    this._syncActualValue();
  }

  _syncDisplayValue() {
    if (this.isNumber()) {
      let newDisplayVal = Units.convertValue(this._value, this.units, this.getDisplayUnits());
      // Round to the field's display precision to prevent floating-point round-trip
      // artifacts from overwriting user-entered values with near-identical noisy values
      // (e.g., 100 cm → 39.37 in → 99.9999... cm should display as 100.00 cm).
      // The underlying _value (imperial) retains full precision; only the display rounds.
      let precision = this.precision;
      if (precision === null || precision === undefined) {
        const displayUnits = this.getDisplayUnits();
        precision = Units._data[displayUnits]?.defaultPrecision ?? DEFAULT_PRECISION;
      }
      this._displayValue = roundToNumDecimals(newDisplayVal, precision);
    }
  }

  _syncActualValue() {
    if (this.isNumber()) {
      this._value = Units.convertValue(this._displayValue, this.getDisplayUnits(), this.units);
      //console.log(`${this.name} - Set actual value to ${this.value} from display value ${this._displayValue}. ` +
      //    `stdUnits: ${this.units}, displayUnits: ${this.getDisplayUnits()}, projUnits: ${this.getProjectUnits()}`);
    }
  }

  _initDefaultValue(data) {
    if ('defaultValue' in data) {
      this.defaultValue = data.defaultValue;
    } else {
      if (this.type == "Select" || this.type == "TypeSelect") {
        if (data.choices) {
          this.defaultValue = data.choices.length > 0 ? Field.getChoiceValue(data.choices[0]) : null;
        } else if (data.choicesFunc) {
          let choices = data.choicesFunc();
          this.defaultValue = choices.length > 0 ? Field.getChoiceValue(choices[0]) : null;
          //console.log(`Setting ${this.name} default: ${this.defaultValue}`);
        } else {
          this.defaultValue = null;
        }
      } else if (this.type == "String") {
        this.defaultValue = "";
      } else {
        this.defaultValue = 0;
      }
    }
  }

  _initUnitsAndMinMax(data) {
    // The 'standard' units are usually given in imperial units.
    // We set the 'display units' as metric if this is a metric project.
    // All calculations and underlying storage are in the standard/imperial 
    // units. But to the UI/user, the units appear as metric if the project is metric.
    let standardUnits = null;
    if ('units' in data) {
      standardUnits = data.units;
    } else {
      if (this.type in kFieldTypesData) {
        standardUnits = kFieldTypesData[this.type].unit;
      } else {
        standardUnits = Units.None;
      }
    }
    this.units = standardUnits;

    // The given data.min and data.max are assumed to be in imperial units.
    // We convert them to the project units if needed.
    // TODO - read data.metricMin, metricMax, if project units are metric
    //console.log(`Field ${this.name} imperial units: ${imperialUnits}, project units: ${this.units}`);

    this.min = valOr(data.min, this._getDefaultMin());
    // Aka: if true, min is inclusive. Otherwise, value must be > min.
    this.allowMin = valOr(data.allowMin, this._getDefaultAllowMin());

    this.max = valOr(data.max, this._getDefaultMax());
  }

  // ObjectUtils calls the field 'enabled', but it is equivalent to 'visible' here
  get enabled() {
    return this.visible;
  }

  set enabled(newVal) {
    this.visible = newVal;
  }

  getValue() {
    return this._value;
  }

  // Mirrors getValue()/getValue()'s counterpart for display units. Subclasses
  // that compute an effective value (e.g. FieldWithDefault) should override
  // both this and getValue() so any generic reader (e.g. the AI assistant's
  // FieldCollector) sees the same value the UI shows.
  getDisplayValue() {
    return this._displayValue;
  }

  // The value that will apply if the field is never explicitly set. Plain
  // instance property for a base Field; FieldWithDefault overrides this to
  // return its live, possibly-computed default instead.
  getDefaultValue() {
    return this.defaultValue;
  }

  setValue(newVal) {
    this._value = newVal;
    this._syncDisplayValue();
  }

  setValueAsUser(newVal) {
    this._value = newVal;
    this._syncDisplayValue();
    // Clears the requiresInput flag
    this.requiresInput = false;
  }

  setDisplayValueAsUser(newDisplayVal) {
    this._displayValue = newDisplayVal;
    this._syncActualValue();
    // Clears the requiresInput flag
    this.requiresInput = false;
  }

  // Interface used by ChangerTool for propose_changes' 'field' change type —
  // the single entry point for "set this field to what the agent proposed",
  // for both a plain Field and any subclass with different write semantics
  // (e.g. FieldWithVariableUnits, which overrides this to also accept a
  // unit alongside the value). Mirrors the non-Field applyAgentValue()
  // convention used by whole-object components like LocationData.
  applyAgentValue(value) {
    if (this.isOutput) {
      throw new Error(`'${this.name}' is a computed/output field and can't be set directly.`);
    }
    if (this.isNumber()) {
      this.setDisplayValueAsUser(Number(value));
    } else if (this.type == FieldType.TypeSelect) {
      this.setValueAsUser(this._resolveTypeSelectAgentValue(value));
    } else {
      this.setValueAsUser(value);
    }
  }

  // A TypeSelect's value is the id of the referenced type (e.g. a wall's
  // WallType), which the agent only sees in this field's choices. Accept an
  // exact type name as well, since that's what the components index shows,
  // and reject anything else: storing an unknown value would leave a dangling
  // reference that shows as an empty selection, with no error.
  _resolveTypeSelectAgentValue(value) {
    const choices = this.getChoices() ?? [];
    const byValue = choices.find(c => c.value == value);
    if (byValue) return byValue.value;
    const byLabel = choices.filter(c => c.label === value);
    if (byLabel.length === 1) return byLabel[0].value;
    const valid = choices.map(c => `${JSON.stringify(c.value)} (${c.label})`).join(', ');
    throw new Error(`${JSON.stringify(value)} is not a valid choice for '${this.name}'. ` +
      `Use one of these values: ${valid || 'none — create one first'}.`);
  }

  static makeName(fieldName, optName) {
    return new Field({
      name: fieldName,
      type: FieldType.String,
      value: optName || 'Untitled',
    });
  }

  // Shortcut for Select type
  static makeSelect(name, enumType, otherOpts) {
    if (!(typeof name == 'string')) {
      throw new Error("Unexpected string for Select name: " + name);
    }
    return new Field({
      name: name,
      type: FieldType.Select,
      choices: makeOptions(enumType),
      ...otherOpts,
    })
  }

  /*
  Every value in typesList should have an id and name.
  Note: only works if the typesList array is kept around (push and clear it but
  not assign a new array).
  */
  static makeTypeSelect(name, typesList, noneOptionName, otherOpts) {
    if (!(typeof name == 'string')) {
      throw new Error("Unexpected string for Select name: " + name);
    }
    let field = new Field({
      name: name,
      type: FieldType.TypeSelect,
      // Use func b/c must be reactive
      choicesFunc: () => {
        let options = typesList.map((elem) => {
          return {label: elem.name.value, value: elem.id};
        })
        if (typeof noneOptionName === 'string') {
          options = [{label: noneOptionName, value: noneOptionName}, ...options]
        }
        // console.log(`New options: ${prettyJson(options)}`)
        return options;
      },
      lookupValueFunc: (value) => {
        for (const elem of typesList) {
          if (elem.id == value) {
            return elem;
          }
        }
        return null;
      },
      ...otherOpts,
    })

    return field;
  }

  static makeOutput(name, type, value, otherOpts) {
    otherOpts = valOr(otherOpts, {});
    let field = new Field({
      name: name,
      type: type,
      value: value,
      isOutput: true,
      ...otherOpts,
    })
    return field;
  }

  static getChoiceValue(choice) {
    return (typeof choice == 'object') ? choice.value : choice;
  }

  getInputType() {
    if (this.type == 'Select' || this.type == 'TypeSelect') {
      return FieldInputType.Select;
    } else if (this.type == 'String') {
      return FieldInputType.String;
    } else if (this.type == 'Number') {
      return FieldInputType.Number;
    } else if (this.type == 'MultiTieredSelect') {
      return 'MultiTieredSelect';
    }
    return FieldInputType.Number;
  }

  isNumber() {
    return this.getInputType() == FieldInputType.Number;
  }

  clampToMinMax() {
    if (this.getInputType() == 'Number') {
      let min = this.getMin();
      let max = this.getMax();
      // Note: allowMin may be false here, but we still clamp to the min, which is fine.
      // The user will be shown an error on the input ("Value must be > 0"). Better than clamping
      // to 0.0001, which is confusing.
      if (min !== null && this._value < min) {
        this._value = min;
        this._syncDisplayValue();
      } else if (max !== null && this._value > max) {
        this._value = max;
        this._syncDisplayValue();
      }
    }
  }

  _getDefaultMin() {
    if (this.type in kFieldTypesData) {
      return valOr(kFieldTypesData[this.type].min, null);
    } else {
      return null;
    }
  }

  _getDefaultAllowMin() {
    if (this.type in kFieldTypesData) {
      return valOr(kFieldTypesData[this.type].allowMin, true);
    } else {
      return true;
    }
  }

  _getDefaultMax() {
    if (this.type in kFieldTypesData) {
      return valOr(kFieldTypesData[this.type].max, null);
    } else {
      return null;
    }
  }

  getMin() {
    return this.min;
  }

  getMax() {
    return this.max;
  }

  requiresWholeNumbers() {
    return this.type == FieldType.Count;
  }

  makeUpdater(effectFunc, opts) {
    return this.watcher.addWatchEffect(() => {
      try {
        effectFunc(this);
        this.internalErrorMsg = null;
      } catch (err) {
        console.error(`Error updating ${this.name}:\n${err}\n${err.stack}`);
        this.internalErrorMsg = `There was an internal error while computing this field. ${PleaseContactStr}\n\nDetails:\n${err}`
        gApp.reportError(err);
      }
    }, opts)
  }

  setVisibility(func) {
    return this.watcher.addWatchEffect(() => {
      try {
        this.visible = func();
      } catch (err) {
        console.error(`Error updating ${this.name} visibility:\n${err}\n${err.stack}`);
        this.internalErrorMsg = `There was an internal error while updating this field. ${PleaseContactStr}\n\nDetails:\n${err}`
        gApp.reportError(err);
      }
    })
  }

  setNameUpdater(func) {
    return this.watcher.addWatchEffect(() => {
      try {
        this.name = func();
      } catch (err) {
        console.error(`Error updating ${this.name} name:\n${err}\n${err.stack}`);
        this.internalErrorMsg = `There was an internal error while updating this field. ${PleaseContactStr}\n\nDetails:\n${err}`
        gApp.reportError(err);
      }
    })
  }

  makeChoicesUpdater(effectFunc) {
    return this.makeUpdater((field) => {
      let newChoices = effectFunc(field);
      this.setChoices(newChoices);
    })
  }

  // Allow changing the choices of a Select type Field.
  // Will change the cur choice if it is no longer valid.
  setChoices(newChoices) {
    this.choices = newChoices;
    let curChoiceValid = false;
    for (const choice of this.choices) {
      if (this._value == Field.getChoiceValue(choice)) {
        curChoiceValid = true;
        break;
      }
    }
    if (!curChoiceValid && newChoices.length > 0) {
      // See if our choice matches any of the new choices, either by value or label. If so, set it to that.
      // Otherwise, set it to the first choice.
      let foundMatch = false;
      for (const choice of this.choices) {
        if (typeof choice == 'object' &&
            (this._value == choice.value || this._value == choice.label)) {
          this._value = Field.getChoiceValue(choice);
          foundMatch = true;
          break;
        } else if (typeof choice == 'string' && this._value == choice) {
          this._value = choice;
          foundMatch = true;
          break;
        }
      }
      if (!foundMatch) {
        this._value = Field.getChoiceValue(this.choices[0]);
      }
    }
  }

  getChoices() {
    if (this.choices) {
      return this.choices;
    } else if (this.choicesFunc) {
      return this.choicesFunc();
    } else {
      return null;
    }
  }

  getSelectLabelStr() {
    // console.log(`Cur value: ${props.modelValue.value}. Choices:\n${selectChoices.value}`);
    for (const choice of this.getChoices()) {
      if (this._value == choice) {
        return choice;
      } else if (this._value == choice.value) {
        return choice.label;
      }
    }
    return this._value;
  }

  isSelectValueValid() {
    for (const choice of this.getChoices()) {
      if (this._value == choice || this._value == choice.value) {
        return true;
      }
    }
    return false;
  }

  lookupValue() {
    if (!this.lookupValueFunc) {
      throw new Error(`Field ${this.name} has no lookupValue func.`);
    }
    return this.lookupValueFunc(this._value);
  }

  getValueStr(targetUnits) {
    /**
     * NOTE - targetUnits is REQUIRED here for Number types, to
     * prevent confusion about which units to return the value in.
     */
    if (this.isNA) {
      return 'N/A';
    }
    if (this.requiresInput || this.waitingInputs) {
      return '--';
    }
    let inputType = this.getInputType();
    if (inputType == FieldInputType.Select) {
      return this.getSelectLabelStr();
    } else if (inputType == FieldInputType.String) {
      return this._value;
    } else if (inputType == FieldInputType.Number) {
      if (!targetUnits) {
        throw new Error("Must specify the target/desired units for getValueStr.");
      }
      return this.getNumberValueStr(targetUnits);
    } else {
      return this._value;
    }
  }

  getDisplayValueStr() {
    return this.getValueStr(this.getDisplayUnits());
  }

  getSpreadsheetValueStr(targetUnits) {
    if (!targetUnits) {
      throw new Error("Must specify the target/desired units for getSpreadsheetValueStr.");
    }
    let valueStr = this.getValueStr(targetUnits);
    if (this.getInputType() == FieldInputType.Number) {
      return Number(valueStr.replace(/,/g, '')); // Remove commas for number conversion
    }
    return valueStr;
  }

  getNumberValueStr(targetUnits) {
    if (!targetUnits) {
      throw new Error("Must specify the target/desired units for getNumberValueStr.");
    }
    let number = this.getValueInUnits(targetUnits);
    let precision = this.precision;
    if (precision === null) {
      precision = Units.getDefaultPrecision(targetUnits);
    }
    let formattedStr = formatNum(number, {maximumFractionDigits: precision});
    return formattedStr;
  }

  convertDisplayUnitsToActual(displayUnitsValue) {
    return Units.convertValue(displayUnitsValue, this.getDisplayUnits(), this.units);
  }

  isWaitingInputs() {
    return this.waitingInputs;
  }

  static inputsReady(fields) {
    for (const field of fields) {
      if (field.requiresInput) {
        return false;
      }
    }
    return true;
  }

  static checkWaitingInputs(fields) {
    return !Field.inputsReady(fields);
  }

  getValueInUnits(targetUnitType) {
    if (!targetUnitType) {
      throw new Error("Must specify the target/desired units.");
    }
    return Units.convertValue(this._value, this.units, targetUnitType);
  }

  setValueInUnits(newVal, targetUnitType) {
    if (targetUnitType in kFieldTypesData) {
      targetUnitType = kFieldTypesData[targetUnitType].unit;
    }
    // Note - don't use this call if you just want to update the display value.
    // This call sets _value first then syncs the display value.
    // Usually fine because this is only called to update output fields.
    this._value = Units.convertValue(newVal, targetUnitType, this.units);
    this._syncDisplayValue();
  }

  getProjectUnits() {
    // If the user gave a parent context, use that to get the project units.
    // Otherwise, use gApp.proj() as the project.
    if (this.projectContext) {
      return this.projectContext.getProjectUnits();
    } else if (gApp && gApp.proj()) {
      return gApp.proj().getProjectUnits();
    } else {
      return ProjectUnits.Imperial;
    }
  }

  convertToProjectUnits(imperialUnits) {
    // Converts the given imperial units to metric if the field is metric and the units
    // have a metric equivalent.
    // Otherwise, returns the imperial units.
    let projectUnits = this.getProjectUnits();
    if (projectUnits == ProjectUnits.Metric) {
      return getMetricUnits(imperialUnits);
    }
    return imperialUnits;
  }

  static convertToAppProjectUnits(imperialUnits) {
    // Converts the given imperial units to metric if the -app- is metric and the units
    // have a metric equivalent.
    // Otherwise, returns the imperial units.
    if (gApp && gApp.proj()) {
      let projUnits = gApp.proj().getProjectUnits();
      if (projUnits == ProjectUnits.Metric) {
        return getMetricUnits(imperialUnits);
      }
      return imperialUnits;
    }
    return imperialUnits;
  }

  static makeProjectValStr(val, units, opts) {
    opts = valOr(opts, {});
    let projectUnits = ProjectUnits.Imperial;
    if (gApp && gApp.proj()) {
      projectUnits = gApp.proj().getProjectUnits();
    }
    let displayUnits = Field.convertToAppProjectUnits(units);
    let convertedValue = Units.convertValue(val, units, displayUnits);
    return makeValStr(convertedValue, displayUnits, opts);
  }

  // Call this event on Field when the units change so that we can correctly update the displayUnits
  onProjectUnitsChanged() {
    // The actual/standard imperial value (_value) stays the same, but the display value changes.
    this._syncDisplayValue();
  }

  getDisplayUnits() {
    return this.convertToProjectUnits(this.units);
  }

  getDisplayUnitsLabel() {
    return Units.getLabel(this.getDisplayUnits());
  }

  roundValueToDisplayPrecision() {
    let precision = this.precision;
    if (precision === null) {
      precision = Units.getDefaultPrecision(this.getDisplayUnits());
    }
    this.displayValue = roundToNumDecimals(this.displayValue, precision);
  }

  _getSelectErrors() {
    let didFind = false;
    let choices = this.getChoices();
    if (choices.length == 0) {
      if (this.errorWhenEmpty) {
        //return [this.errorWhenEmpty];
        return ['Select a value'];
      }
      // The field is NA for now. Allow any value.
      return [];
    }
    if (this._value == null) {
      return ['Select a value.'];
    }

    let allChoices = choices;
    // Hidden choices are valid (will not cause an error), but the user will not be able to set
    // the field to a hidden choice through the UI. (Used for programmatically-set choices)
    if (this.hiddenChoices) {
      extendArray(allChoices, this.hiddenChoices);
    }
    for (const option of allChoices) {
      let optionValue = (typeof option == 'object') ? option.value : option;
      if (this._value == optionValue) {
        didFind = true;
        break;
      }
    }
    if (!didFind) {
      return [`Invalid option.`];
    }
    return [];
  }

  _getNumberErrors() {
    let min = this.getMin();
    if (min !== null) {
      if (this.allowMin) {
        if (this._value < min) {
          return [`Value must be >= ${min}`];
        }
      } else {
        if (this._value <= min) {
          return [`Value must be > ${min}`];
        }
      }
    }
    let max = this.getMax();
    if (max !== null && this._value > max) {
      return [`Value must be <= ${max}`];
    }
    return [];
  }

  getObjErrors(opts) {
    opts = valOr(opts, {});
    /*
    Returns a list of errors, for the project error tracker.
    */
    if (!this.visible) {
      return [];
    }
    let errors = [];
    for (const msg of this.errorsMap.values()) {
      errors.push(msg);
    }
    if (this.internalErrorMsg) {
      errors.push(this.internalErrorMsg)
    }
    if (this.requiresUserInput() && !this.isOutput) {
      errors.push(`You must fill in this value.`);
      return errors;
    }
    if (this.waitingInputs) {
      errors.push(`Waiting for required inputs.`);
      return errors;
    }
    // console.log(`GETTING ERRORS`);
    let inputType = this.getInputType();
    if (inputType == FieldInputType.Select) {
      extendArray(errors, this._getSelectErrors());
    } else if (inputType == FieldInputType.Number) {
      // console.log(`Adding number errors for ${this.name}`);
      extendArray(errors, this._getNumberErrors());
    }
    extendArray(errors, this.getOtherObjErrors());
    return errors;
  }

  getOtherObjErrors() {
    // Override in subclass if relevant
    return [];
  }

  requiresUserInput() {
    // True if the field requires user input.
    // When false, do not show an error msg underneath the field (can show
    // after the user has entered their value).

    if (this.type === FieldType.TypeSelect) {
      // Special handling here
      let currentValue = this.lookupValue();
      if (currentValue === null && this.errorWhenEmpty) {
        return true;
      }
      return false;
    }
    return this.requiresInput;
  }

  setErrorMsg(key, msg) {
    /*
    Sets an error msg for the given key.
    */
    if (msg === null) {
      this.errorsMap.delete(key);
      return;
    }
    this.errorsMap.set(key, msg);
  }

  clearErrorMsg(key) {
    /*
    Clears the error msg for the given key.
    */
    this.errorsMap.delete(key);
  }

  setPriorityErrorMsg(msg) {
    // Shortcut to set a priority error msg (should show up first).
    this.setErrorMsg('_priorityError', msg);
  }

  clearPriorityErrorMsg() {
    // Shortcut to clear the priority error msg.
    this.clearErrorMsg('_priorityError');
  }

  getErrorString() {
    // We show the first error msg only from the
    // list of all errors.
    let allErrors = this.getObjErrors();
    if (allErrors.length > 0) {
      return allErrors[0];
    }
    return null;
  }

  makeSelectionObject() {
    /**
     * Returns helper interface that is used with various components that
     * handle selections.
     */
    let field = this;
    return {
      getValue: () => {
        return this.getValue();
      },
      setValue: (newVal) => {
        this.setValue(newVal);
      },
      isValidValue: () => {
        return field.isSelectValueValid();
      },
      getChoices: () => {
        return field.getChoices();
      },
      getNoneValue: () => {
        /**
         * Returns null if 'None' not allowed
         */
        let choices = field.getChoices();
        if (choices.length == 0) {
          return null;
        }
        if (choices[0].value == 'None' || choices[0].value == 'None') {
          return choices[0].value;
        }
        return null;
      },
    }
  }
};

export class FieldGroup {
  constructor(fields, opts) {
    opts = valOr(opts, {});
    this.fields = fields;

    this.childObjs = '$auto'
    let basePath = valOr(opts.basePath, 'fieldGroup');
    this.objInfo = {
      '_name': null,
      path: basePath,
    }
  }

  static fromDict(fieldsDict, opts) {
    let fields = [];
    for (const key in fieldsDict) {
      let field = fieldsDict[key];
      field.key = key;
      fields.push(field);
    }
    return new FieldGroup(fields, opts);
  }

  writeToJson() {
    let obj = {};
    for (const field of this.fields) {
      obj[field.key] = ser.writeToJson(field);
    }
    return obj;
  }

  readFromJson(obj) {
    // console.log(`Reading fieldGroup:\n${prettyJson(obj)}`);
    for (const field of this.fields) {
      if (field.key in obj) {
        // console.log(`Reading key: ${field.key}`);
        ser.readFromJson(field, obj[field.key]);
      }
    }
  }

  getField(fieldKey) {
    for (const field of this.fields) {
      if (field.key == fieldKey) {
        return field;
      }
    }
    throw new Error(`Could not find field: ${fieldKey}. Fields:\n${this.fields.map((elem) => { return elem.key})}`);
  }

  get(fieldKey) {
    return this.getField(fieldKey);
  }

  setVisibility(func) {
    return gApp.proj().addWatchEffect(() => {
      try {
        let isVisible = func();
        for (const field of this.fields) {
          field.visible = isVisible;
        }
      } catch (err) {
        console.error(`Error updating FieldGroup visibility:\n${err}\n${err.stack}`);
        gApp.reportError(err);
      }
    })
  }
};

export class SelectOrManualInput {
    init(name, optionsData, inputType) {
        this.optionsData = optionsData;

        // TODO - improve this (maybe make a single field?)
        this.optionPicker = Field.makeSelect(name, optionsData)
        this.value = new Field({
            name: 'Value',
            type: inputType
        })
        this.value.makeUpdater((field) => {
          let curOption = this.optionPicker.value;
          if (curOption !== 'Manual') {
            field.isOutput = true;
            field.value = this.optionsData._data[curOption].value;
          } else {
            field.isOutput = false;
          }
        });

        this.serFields = [
            'optionPicker',
            'value',
        ]
        this.childObjs = '$auto'
    }

    getValue() {
      return this.value.value;
    }

    isManual() {
      return this.optionPicker.value == 'Manual';
    }

    setVisibility(func) {
      return gApp.proj().addWatchEffect(() => {
        try {
          let isVisible = func();
          this.optionPicker.visible = isVisible;
          this.value.visible = isVisible;
        } catch (err) {
          console.error(`Error updating SelectOrManual visibility:\n${err}\n${err.stack}`);
          gApp.reportError(err);
        }
      })
    }
}
setupClass(SelectOrManualInput)
